import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { todayStr } from '../services/reportsApi';
import { getCurrencySymbol } from '../utils/currency';

const findToken = (obj: any, depth = 0): string | null => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    const tokenKeys = ['accessToken', 'access_token', 'token', 'jwtToken', 'jwt', 'bearerToken', 'authToken', 'userToken', 'sessionToken', 'Authorization', 'authorization', 'idToken', 'id_token'];
    for (const key of tokenKeys) {
        if (obj[key] && typeof obj[key] === 'string' && obj[key].length > 10) return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (typeof val === 'string' && val.length > 50) return val;
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findToken(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

const findLocationId = (obj: any, depth = 0): string | null => {
    if (!obj || typeof obj !== 'object' || depth > 3) return null;
    for (const key of ['locationId', 'location_id', 'locationID', 'locId', 'outletId', 'outlet_id']) {
        if (obj[key] && typeof obj[key] === 'string') return obj[key];
    }
    for (const [, val] of Object.entries(obj)) {
        if (val && typeof val === 'object' && !Array.isArray(val)) {
            const found = findLocationId(val, depth + 1);
            if (found) return found;
        }
    }
    return null;
};

export const useDrillAuth = () => {
    const { auth } = useAuth();
    const { selectedBranch } = useBranch();
    const { state } = useLocation();
    const locationId = useMemo(() => {
        const branchId = selectedBranch?.id ?? selectedBranch?.locationId ?? null;
        return branchId || findLocationId(auth) || '';
    }, [auth, selectedBranch]);
    const token = useMemo(() => (auth ? findToken(auth) : null), [auth]);
    const selectedDate = state?.date && /^\d{4}-\d{2}-\d{2}$/.test(state.date) ? state.date : todayStr();
    const dateFilter = typeof state?.dateFilter === 'string' && state.dateFilter.trim() ? state.dateFilter : 'Today';
    const snapshotValues = state?.snapshotValues ?? null;
    return { locationId, token, today: selectedDate, selectedDate, dateFilter, snapshotValues };
};

const unwrap = (res: any): any => {
    if (res == null) return res;
    const d = res.metaDataInfo?.data ?? res.responseBody ?? res.content ?? res.data ?? res.result ?? res.response;
    if (d != null && d !== res) return d;
    return res;
};

export const pick = (res: any, ...keys: string[]): any => {
    if (res == null) return null;
    const r = unwrap(res);
    for (const key of keys) {
        const v = r[key];
        if (v !== undefined && v !== null) return v;
    }
    const raw = Array.isArray(r) ? r : (r?.content ?? r?.data ?? r);
    const obj = Array.isArray(raw) ? raw[0] : raw;
    if (!obj || typeof obj !== 'object') return null;
    for (const key of keys) {
        const v = obj[key];
        if (v !== undefined && v !== null) return v;
    }
    return null;
};

export const listFrom = (res: any): any[] => {
    if (res == null) return [];
    const r = unwrap(res);
    let raw = r?.content ?? r?.data ?? r?.items ?? r?.records ?? r?.categories ?? r?.channelList ?? r?.employees ?? r?.staff ?? r?.staffList ?? r?.staffTips ?? r?.tipDetails ?? r?.voids ?? r?.voidList ?? r?.voidDetails ?? r?.voidItems ?? r?.orders ?? r?.orderList ?? r?.canceledOrders ?? (Array.isArray(r) ? r : null);
    if (!Array.isArray(raw) && r && typeof r === 'object') {
        for (const val of Object.values(r)) {
            if (Array.isArray(val)) {
                raw = val;
                break;
            }
        }
    }
    return Array.isArray(raw) ? raw : [];
};

const STAFF_KEYS = ['staffName', 'name', 'fullName', 'employeeName', 'userName', 'firstName', 'userId', 'employeeId', 'staffId', 'id'];
const looksLikeStaff = (obj: any): boolean => obj && typeof obj === 'object' && STAFF_KEYS.some((k) => obj[k] != null);

function findStaffArray(val: any, depth = 0): any[] | null {
    if (depth > 4 || val == null) return null;
    if (Array.isArray(val) && val.length > 0 && looksLikeStaff(val[0])) return val;
    if (typeof val === 'object' && !Array.isArray(val)) {
        for (const k of ['content', 'data', 'employees', 'staff', 'staffList', 'activityList', 'list', 'items', 'records']) {
            const found = findStaffArray(val[k], depth + 1);
            if (found) return found;
        }
        for (const v of Object.values(val)) {
            const found = findStaffArray(v, depth + 1);
            if (found) return found;
        }
    }
    return null;
}

export const staffListFrom = (res: any): any[] => {
    if (res == null) return [];
    const r = unwrap(res);
    let raw =
        r?.content ?? r?.data ?? r?.employees ?? r?.staff ?? r?.staffList ?? r?.activityList ?? r?.list
        ?? (r?.data && Array.isArray(r.data) ? r.data : null)
        ?? (typeof r?.data === 'object' && r?.data && (r.data.employees ?? r.data.staff ?? r.data.content) ? (r.data.employees ?? r.data.staff ?? r.data.content) : null)
        ?? (Array.isArray(r) ? r : null);
    if (!Array.isArray(raw) || raw.length === 0) raw = findStaffArray(r) ?? findStaffArray(res) ?? [];
    return Array.isArray(raw) ? raw : [];
};

export const fmtCurrency = (v: any): string =>
    v != null && v !== '' ? `${getCurrencySymbol()}${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : `${getCurrencySymbol()}0.00`;

// Table-cell amount: whole numbers drop decimals ($27), fractional keep 2 ($27.50).
// Matches the export formatting rule for monetary table columns.
export const fmtCurrencyCell = (v: any): string => {
    const n = Number(v ?? 0) || 0;
    return Number.isInteger(n)
        ? `${getCurrencySymbol()}${n.toLocaleString('en-US')}`
        : `${getCurrencySymbol()}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const fmtNum = (v: any): string => (v != null && v !== '' ? Number(v).toLocaleString('en-US') : '0');

// Human-readable active date range for the "All outlets" export file header —
// preset names (e.g. "Today", "Yesterday") pass through as-is; a "custom:start:end"
// filter string, or an explicit { startDate, endDate }, formats as "Jul 12 - Jul 14".
export const getExportDateRangeLabel = (
    dateFilter: string | undefined,
    dateRange: { startDate?: string; endDate?: string } | null | undefined,
): string => {
    const fmt = (d?: string) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '');
    if (typeof dateFilter === 'string' && dateFilter.startsWith('custom:')) {
        return dateRange?.startDate === dateRange?.endDate ? fmt(dateRange?.startDate) : `${fmt(dateRange?.startDate)} - ${fmt(dateRange?.endDate)}`;
    }
    if (dateFilter) return dateFilter;
    if (dateRange?.startDate) {
        return dateRange.startDate === dateRange.endDate ? fmt(dateRange.startDate) : `${fmt(dateRange.startDate)} - ${fmt(dateRange.endDate)}`;
    }
    return 'Today';
};

